// How a lab report is shown on a Test (TGCLOUD-1866, Phase 2 rework). Pure: no React, no
// requests. Shared by the review that applies a report to the Test form (LabReportApply) and the
// Test's list of the reports applied to it (LabReportsList), so the two say the same thing the same
// way. What applying does to the form is applyProposal.js.
//
// VERBATIM FIRST. The lab's own wording (every `*AsPrinted`, `labClassification.asPrinted` above
// all) is what is shown, untouched. A coded value (a conclusion's meaning, a coverage status) is
// shown BESIDE it as a label, never instead of it.
//
// NOTHING IS INFERRED HERE. A genome build or a transcript version the report does not give is
// asked for, with no default: the save records it as supplied by the person who applied the report
// (backend labReportRecord.js), and a pre-selected build would be a guess recorded as their answer.

// The backend's own lists (backend/services/testResults/rules.js), kept in one place with the rest
// of the Test record's vocabulary. testRecordModel.test.js and labReportDisplay.test.js read
// rules.js and fail if they drift from it.
import {
  GENOME_BUILDS,
  TRANSCRIPT_PATTERN_SOURCE,
  isVersionedTranscript,
  METHOD_KIND_LABELS
} from '../clinical-notes-section/forms/test-record/testRecordModel';
import { english } from 'src/i18n/language';

// The words here are English UI text. A label is translated where it is shown; a helper that
// builds a sentence takes the caller's `t` (English by default), and a value from the report or
// the record goes into the sentence as it is.

export {
  GENOME_BUILDS,
  TRANSCRIPT_PATTERN_SOURCE,
  isVersionedTranscript,
  METHOD_KIND_LABELS as METHOD_LABELS
};

export const CONCLUSION_LABELS = Object.freeze({
  positive: 'Positive',
  negative: 'Negative',
  inconclusive: 'Inconclusive',
  'not-stated': 'Not stated'
});
export const NOT_READ_KIND_LABELS = Object.freeze({
  'copy-number': 'Copy-number findings',
  pharmacogenomic: 'Pharmacogenomic results',
  repeat: 'Repeat expansions',
  'image-only': 'Image-only content',
  other: 'Not read'
});

export const AI_VALUE_LABEL = 'suggested by AI, found in the report';

// ── The identity check (TGCLOUD-1868, decision 29) ─────────────────────────────────────────────
//
// Intake and the inbox hand-off answer `identity: { compared, mismatch, ..., requiresAcknowledgement,
// reasons: [{ code, acknowledge }] }`: FLAGS AND CODES, never a value. Only a real disagreement asks
// for "I have checked: this is this person's report", and only then does the save send
// `acknowledgeIdentityMismatch`:
//   acknowledged   a printed surname, date of birth, national ID or hospital number that disagrees
//                  with the record; the role on the report not fitting the record's sex; not the
//                  person of the report the check proposed; a single-fetus report whose mother
//                  could not be compared with the record (TGCLOUD-1868 review)
//   information    nothing could be compared, or only the given name differs (a nickname): said, and
//                  never a box to tick
// An answer from before decision 29 carries flags and no reasons: then any mismatch is held to the
// acknowledgement, as the save held it.

// The codes, as the backend sends them: those a save holds to the acknowledgement, and those it
// never does.
export const IDENTITY_ACKNOWLEDGED_REASONS = Object.freeze([
  'surname',
  'dob',
  'national-id',
  'hospital-number',
  'role-sex',
  'not-proposed',
  'mother-uncompared'
]);
export const IDENTITY_INFORMATION_REASONS = Object.freeze([
  'uncompared',
  'given-name'
]);

// Each reason in words. A printed value that disagrees names the value, never prints it.
export const IDENTITY_REASON_TEXT = Object.freeze({
  surname: 'The surname on the report does not match this person’s record.',
  dob: 'The date of birth on the report does not match this person’s record.',
  'national-id':
    'The national ID on the report does not match this person’s record.',
  'hospital-number':
    'The hospital number on the report does not match this person’s record.',
  'role-sex':
    'The person chosen on the report does not fit the sex this record gives.',
  'not-proposed':
    'Another person on the report fits this record’s details better than the one chosen.',
  'mother-uncompared':
    'Nothing the report prints about the mother could be compared with this record. Check her name, date of birth and national ID on the report.',
  uncompared:
    'Nothing the report prints about this person could be compared with this record. Check the name and date of birth on the report.',
  'given-name':
    'Only the given name on the report differs from this record. It may be a shortened or familiar name: check it on the report.'
});
// Said before the reasons that need the acknowledgement (with a colon before a list of them, a full
// stop in a sentence), and for a reason this list cannot name.
export const IDENTITY_MAY_BE_SOMEONE_ELSES =
  'This may be someone else’s report';
// The same, before the list of reasons.
export const IDENTITY_MAY_BE_SOMEONE_ELSES_LEAD =
  'This may be someone else’s report:';
export const IDENTITY_UNNAMED_REASON =
  'The report differs from this person’s record.';
export const IDENTITY_ACKNOWLEDGEMENT =
  'I have checked: this is this person’s report.';

const reasonWords = (code) =>
  (typeof code === 'string' &&
    Object.prototype.hasOwnProperty.call(IDENTITY_REASON_TEXT, code) &&
    IDENTITY_REASON_TEXT[code]) ||
  IDENTITY_UNNAMED_REASON;
const uniqueList = (list) => [...new Set(list)];

/**
 * Whether the save of this reading must send the acknowledgement: the answer's own
 * `requiresAcknowledgement`, else (an answer from before decision 29) any mismatch.
 */
export function requiresIdentityAcknowledgement(identity) {
  const id = identity || {};
  return typeof id.requiresAcknowledgement === 'boolean'
    ? id.requiresAcknowledgement
    : !!id.mismatch;
}

/**
 * The identity check, split as the review shows it:
 * `{ required, acknowledge: string[], information: string[], legacy }`. `acknowledge` is the reasons
 * that need "I have checked: this is this person's report", `information` those said as text only,
 * each in words. `legacy` is an answer from before decision 29 (flags, no reasons): its one
 * sentence is identityCheckText's.
 */
export function identityCheckOf(identity) {
  const id = identity || {};
  const required = requiresIdentityAcknowledgement(id);
  if (!Array.isArray(id.reasons)) {
    return { required, acknowledge: [], information: [], legacy: true };
  }
  const reasons = id.reasons.filter(
    (r) => r && typeof r.code === 'string' && r.code
  );
  const held = uniqueList(
    reasons.filter((r) => r.acknowledge === true).map((r) => r.code)
  );
  const told = uniqueList(
    reasons.filter((r) => r.acknowledge !== true).map((r) => r.code)
  );
  // The server says whether its save needs the acknowledgement. A reason it holds to one while it
  // says none is needed is still said, as information.
  const acknowledge = required ? held : [];
  const information = required ? told : uniqueList([...held, ...told]);
  return {
    required,
    acknowledge: required
      ? acknowledge.length
        ? acknowledge.map(reasonWords)
        : [IDENTITY_UNNAMED_REASON]
      : [],
    information: information.map(reasonWords),
    legacy: false
  };
}

// An answer from before decision 29, said from its identity FLAGS (never a value): the name, the
// date of birth or a national id that does not match the record; and, for one person's part of a
// report about several uploaded on a Test (TGCLOUD-1868 review), that nothing the report prints about
// them could be compared (`uncompared`), that the record's sex does not fit their role
// (`roleSexMismatch`), or that another person on the report fits the record better (`notProposed`).
//
// What disagrees, as one whole sentence for each set of flags (keyed by the parts, in order).
const LEGACY_MISMATCH_TEXT = Object.freeze({
  name: 'The name on this report does not match this person’s record. It may be someone else’s report.',
  dob: 'The date of birth on this report does not match this person’s record. It may be someone else’s report.',
  nationalId:
    'The national ID on this report does not match this person’s record. It may be someone else’s report.',
  'name+dob':
    'The name and date of birth on this report do not match this person’s record. It may be someone else’s report.',
  'name+nationalId':
    'The name and national ID on this report do not match this person’s record. It may be someone else’s report.',
  'dob+nationalId':
    'The date of birth and national ID on this report do not match this person’s record. It may be someone else’s report.',
  'name+dob+nationalId':
    'The name, date of birth and national ID on this report do not match this person’s record. It may be someone else’s report.',
  none: 'The name or date of birth on this report does not match this person’s record. It may be someone else’s report.'
});

function legacyIdentityCheckText(identity, t = english) {
  const id = identity || {};
  if (!id.mismatch) return '';
  const parts = [
    id.surnameMismatch || id.givenNameMismatch ? 'name' : null,
    id.dobMismatch ? 'dob' : null,
    id.nationalIdMismatch ? 'nationalId' : null
  ].filter(Boolean);
  const partOfReport = !!(
    id.uncompared ||
    id.roleSexMismatch ||
    id.notProposed
  );
  const sentences = [];
  if (parts.length || !partOfReport) {
    sentences.push(
      t(LEGACY_MISMATCH_TEXT[parts.length ? parts.join('+') : 'none'])
    );
  }
  if (id.uncompared) {
    sentences.push(
      t(
        'Nothing the report prints about the person chosen could be compared with this record.'
      )
    );
  }
  if (id.roleSexMismatch) {
    sentences.push(
      t(
        'The person chosen on the report does not fit the sex this record gives.'
      )
    );
  }
  if (id.notProposed) {
    sentences.push(
      t(
        'Another person on the report fits this record’s details better than the one chosen.'
      )
    );
  }
  sentences.push(
    partOfReport
      ? t(
          'Check that this is this person’s part of the report before saving the Test.'
        )
      : t('Check it before saving the Test.')
  );
  return sentences.join(' ');
}

/**
 * Why the report may not be this person's, in words (decision 29 above): the reasons that need the
 * acknowledgement after "This may be someone else's report.", then those said as information only.
 * '' when there is nothing to say. An answer from before decision 29 is said from its flags
 * (legacyIdentityCheckText). `t` is the caller's.
 */
export function identityCheckText(identity, t = english) {
  const check = identityCheckOf(identity);
  if (check.legacy) return legacyIdentityCheckText(identity, t);
  return [
    ...(check.acknowledge.length
      ? [
          t('This may be someone else’s report.'),
          ...check.acknowledge.map((text) => t(text))
        ]
      : []),
    ...check.information.map((text) => t(text)),
    ...(check.required ? [t('Check it before saving the Test.')] : [])
  ].join(' ');
}

// ── What the genotype record did not take ───────────────────────────────────────────────────────

/**
 * The variants whose call the genotype record kept from another Test, said in one sentence, or
 * '' when there are none. Takes a Test save's `genotypeCopy.kept` ({ gene, variantC, variantP,
 * variantG, ... }; backend genotypeVariantService.upsertVariantsIntoGenotypeWithOutcomes), and an
 * item naming its `variant` in one string alike. The call the record keeps, and where from, are
 * the record's own words; `t` is the caller's.
 */
export function keptVariantsMessage(kept, t = english) {
  const list = (Array.isArray(kept) ? kept : []).filter(Boolean);
  if (!list.length) return '';
  const each = list.map((k) => {
    const variant = k.variant || k.variantC || k.variantP || k.variantG || '';
    const call = k.heldCall || t('no classification');
    let held;
    if (k.heldBy && k.originDiffers) {
      held = t(
        'the record keeps “{{call}}” from {{heldBy}}, which gives a different origin',
        { call, heldBy: k.heldBy }
      );
    } else if (k.heldBy) {
      held = t('the record keeps “{{call}}” from {{heldBy}}', {
        call,
        heldBy: k.heldBy
      });
    } else if (k.originDiffers) {
      held = t('the record keeps “{{call}}”, which gives a different origin', {
        call
      });
    } else {
      held = t('the record keeps “{{call}}”', { call });
    }
    return `${[k.gene, variant].filter(Boolean).join(' ')} (${held})`;
  });
  return t('Not applied to the genotype record: {{variants}}.', {
    variants: each.join('; ')
  });
}

// ── Paths ───────────────────────────────────────────────────────────────────────────────────────

/** The value at a dotted path ('variants.0.hgvsAsPrinted.c'), or undefined. */
export const getPath = (object, path) =>
  String(path || '')
    .split('.')
    .reduce((v, key) => (v == null ? undefined : v[key]), object);

/**
 * A copy of `object` with `value` at `path`, creating plain objects (or arrays, where the next
 * key is an index) on the way. Nothing else is copied deeper than the path.
 */
export function setPath(object, path, value) {
  const keys = String(path).split('.');
  const put = (node, i) => {
    const key = keys[i];
    const base = Array.isArray(node)
      ? [...node]
      : node && typeof node === 'object'
      ? { ...node }
      : /^\d+$/.test(key)
      ? []
      : {};
    base[key] = i === keys.length - 1 ? value : put(base[key], i + 1);
    return base;
  };
  return put(object, 0);
}

// ── Who read a value ────────────────────────────────────────────────────────────────────────────

/**
 * The proposal paths the AI pass added. Only a value the AI contributed is ever labelled AI:
 * everything else was read by the rules, and a review that called it AI would be wrong.
 */
export const aiPathsOf = (fieldSources) =>
  new Set(
    (Array.isArray(fieldSources) ? fieldSources : [])
      .filter((s) => s && s.source === 'ai' && typeof s.path === 'string')
      .map((s) => s.path)
  );

/** Did the AI pass supply `path`, or anything inside it? */
export const isAiValue = (aiPaths, path) => {
  if (!aiPaths || !aiPaths.size || !path) return false;
  if (aiPaths.has(path)) return true;
  for (const p of aiPaths) if (p.startsWith(`${path}.`)) return true;
  return false;
};

// ── Dates ───────────────────────────────────────────────────────────────────────────────────────

/**
 * A report's date, as the site formats dates, else as the report printed it. `date` is a calendar
 * 'YYYY-MM-DD' string, which `fmtDate` (useDateFormatter) reads by its calendar parts; a date the
 * reader could not read unambiguously has only `asPrinted`, shown verbatim rather than guessed.
 */
export const reportDateText = (reportDate, fmtDate) => {
  if (!reportDate) return '';
  if (reportDate.date) return fmtDate(reportDate.date);
  return reportDate.asPrinted || '';
};
